/**
 * La logique pure de la séance à deux : où l'on en est, et ce que disent les
 * boutons. Séparée de l'écran pour être testée sans DOM.
 *
 * Tout ce qui décide est **côté serveur** — le prochain échelon d'indice, le
 * droit de dire « a cherché sans trouver », l'ouverture du corrigé. Ce fichier ne
 * fait que lire `SeanceADeuxOut` : recalculer l'un de ces droits dans la coquille
 * ferait deux règles qui finissent par diverger, et le bouton afficherait une
 * possibilité que le serveur refuse.
 */

import type { SeanceADeuxOut } from "@/api/schema";

/** Les trois temps d'une séance : chercher, corriger, jugée. */
export type Etape = "chercher" | "corriger" | "jugee";

export function etapeDe(seance: SeanceADeuxOut): Etape {
  if (seance.verdict) return "jugee";
  if (seance.corrige_ouvert) return "corriger";
  return "chercher";
}

/**
 * Le libellé du bouton d'indice, ou `null` quand il n'y a plus rien à dévoiler.
 *
 * Le rang de l'échelon est dit (« l'indice 2 sur 3 ») : c'est l'écran du parent,
 * et savoir qu'il reste un seul indice change la façon de le donner. Ce n'est
 * pas une note — rien ici ne mesure l'enfant.
 */
export function boutonDIndice(seance: SeanceADeuxOut): string | null {
  if (seance.verdict || !seance.prochain_indice) return null;
  const total = seance.indices_total;
  if (seance.prochain_indice === 1) return `Voir le premier indice (sur ${total})`;
  return `Voir l'indice ${seance.prochain_indice} sur ${total}`;
}

/** Pourquoi « a cherché sans trouver » n'est pas encore proposé, ou `null`. */
export function pourquoiPasEncore(seance: SeanceADeuxOut, prenom: string): string | null {
  if (seance.tentative || seance.peut_declarer_cherche) return null;
  return `« ${prenom} a cherché sans trouver » s'ouvre une fois tous les indices donnés : le corrigé vient après l'aide, pas à sa place.`;
}

/** La ligne qui dit ce qui a été indiqué, une fois la tentative posée. */
export function tentativeEnMots(seance: SeanceADeuxOut, prenom: string): string | null {
  if (seance.tentative === "reponse") return `${prenom} a donné une réponse.`;
  if (seance.tentative === "cherche") return `${prenom} a cherché sans trouver, avec tous les indices.`;
  return null;
}

/** Ce qui s'affiche quand l'exercice n'a aucun indice. */
export function sansIndice(seance: SeanceADeuxOut): string | null {
  if (seance.indices_total > 0) return null;
  return "Cet exercice n'a pas d'indices écrits. Reformulez la question, montrez un exemple du cours, puis laissez chercher.";
}
